import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { TagResponse } from '../../services/tag.service';

@Component({
  selector: 'app-tag-filter',
  imports: [
    FormsModule,
    MatFormField,
    MatIcon,
    MatOption,
    MatPrefix,
    MatSelect,
    MatSelectTrigger,
  ],
  templateUrl: './tag-filter.component.html',
  styleUrl: './tag-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagFilterComponent {
  /** Full tag list to display in the dropdown. Pass TagService.tags(). */
  readonly tags = input<TagResponse[]>([]);

  /**
   * Width of the dropdown panel.
   * - Default ('') — matches the trigger width (Angular Material default).
   * - null — auto-sizes to the widest option.
   * - '280px' / 280 — explicit fixed width.
   */
  readonly panelWidth = input<string | number | null>(null);

  /** When true the select is rendered but non-interactive (e.g. published interaction). */
  readonly disabled = input(false);

  /**
   * Two-way binding for the selected tag IDs.
   * Emits selectedTagIdsChange whenever the selection changes.
   * Use (selectedTagIdsChange) or [(selectedTagIds)] in the parent.
   */
  readonly selectedTagIds = model<string[]>([]);

  protected readonly searchTerm = signal('');

  private readonly _searchInput =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly filteredTags = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();

    const source = term
      ? this.tags().filter((t) => t.label.toLowerCase().includes(term))
      : this.tags();

    return [...source].sort((a, b) => a.label.localeCompare(b.label));
  });

  // Used internally by triggerLabel — not exposed to parent.
  private readonly _selectedTags = computed(() =>
    this.tags().filter((t) => this.selectedTagIds().includes(t.id))
  );

  protected readonly triggerLabel = computed(() => {
    const selected = this._selectedTags();
    if (selected.length === 0) return null;
    if (selected.length === 1) return selected[0].label;
    return `${selected[0].label} +${selected.length - 1}`;
  });

  protected handleSelectionChange(ids: string[]): void {
    // Preserve selections that are currently filtered out (not visible in the dropdown).
    // MatSelect only emits IDs for visible options, so we merge back any hidden ones.
    const visibleIds = new Set(this.filteredTags().map((t) => t.id));
    const hiddenSelectedIds = this.selectedTagIds().filter((id) => !visibleIds.has(id));
    this.selectedTagIds.set([...hiddenSelectedIds, ...ids]);
  }

  protected handlePanelOpenedChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => this._searchInput()?.nativeElement.focus(), 0);
    } else {
      this.searchTerm.set('');
    }
  }

  protected stopPropagation(event: KeyboardEvent): void {
    event.stopPropagation();
  }
}
